import React, { useState, useMemo } from 'react';
import { useFetch } from '../../hooks/useApiCall';
import { getCategories } from '../../services/serviceService';
import { mapCategoriesToPriceData } from '../../utils/dataMappers';
import ArtistPickerDialog from './ArtistPickerDialog';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Spinner from 'react-bootstrap/Spinner';
import Badge from 'react-bootstrap/Badge';
import {
  Palette, Eye, Sparkles, Gem, Scissors, Star,
  X, ChevronDown, User,
} from 'lucide-react';

const getCategoryIcon = (name) => {
  const lower = (name || '').toLowerCase();
  if (lower.includes('makeup') || lower.includes('maquillaje')) return Palette;
  if (lower.includes('lash') || lower.includes('brow') || lower.includes('ceja') || lower.includes('pestana')) return Eye;
  if (lower.includes('face') || lower.includes('body') || lower.includes('facial') || lower.includes('corporal')) return Sparkles;
  if (lower.includes('nail') || lower.includes('una') || lower.includes('uña')) return Gem;
  if (lower.includes('hair') || lower.includes('cabello') || lower.includes('pelo')) return Scissors;
  return Star;
};

const ServiceWithArtistSelector = ({ selectedServices, onServicesChange, onArtistDialogOpen }) => {
  const { data: apiCategories, loading, error, refetch } = useFetch(getCategories);

  const priceData = useMemo(
    () => apiCategories ? mapCategoriesToPriceData(apiCategories) : [],
    [apiCategories]
  );

  const [selectedCatIndex, setSelectedCatIndex] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [artistDialog, setArtistDialog] = useState({ open: false, index: null });

  const selectedCategory = selectedCatIndex !== null ? priceData[selectedCatIndex] : null;

  useMemo(() => {
    if (priceData.length > 0 && selectedCatIndex === null) {
      setSelectedCatIndex(0);
      if (priceData[0].sub_services?.length > 0) {
        setSelectedSubCategory(priceData[0].sub_services[0]);
      }
    }
  }, [priceData, selectedCatIndex]);

  const handleCategorySelect = (idx) => {
    setSelectedCatIndex(idx);
    const cat = priceData[idx];
    setSelectedSubCategory(cat.sub_services?.length > 0 ? cat.sub_services[0] : null);
  };

  const visibleServices = useMemo(() => {
    if (!selectedCategory) return [];
    if (selectedCategory.sub_services?.length > 0 && selectedSubCategory) {
      return selectedSubCategory.services || [];
    }
    if (!selectedCategory.sub_services?.length && selectedCategory.services?.length > 0) {
      return selectedCategory.services;
    }
    return [];
  }, [selectedCategory, selectedSubCategory]);

  const buildLabel = (serviceName) => {
    let label = selectedCategory?.service_name || '';
    if (selectedSubCategory?.sub_service_name) label += ' / ' + selectedSubCategory.sub_service_name;
    label += ' / ' + serviceName;
    return label;
  };

  const handleAddService = (svc) => {
    if (!svc.id) return; // Skip services without backend ID
    const label = buildLabel(svc.name);
    const newEntry = {
      label,
      shortName: svc.name,
      categoryName: selectedCategory?.service_name || '',
      subCategoryName: selectedSubCategory?.sub_service_name || '',
      serviceId: svc.id,
      employeeId: null,
      employeeName: null,
      price: svc.price || null,
    };
    onServicesChange([...selectedServices, newEntry]);
    // Ofrecer la artista en el momento de seleccionar el servicio
    // (la primera opción del diálogo es «Sin preferencia»).
    setArtistDialog({ open: true, index: selectedServices.length });
  };

  const handleRemoveService = (index) => {
    onServicesChange(selectedServices.filter((_, i) => i !== index));
  };

  const handleArtistSelect = (artistData) => {
    if (artistDialog.index === null) return;
    const updated = [...selectedServices];
    updated[artistDialog.index] = {
      ...updated[artistDialog.index],
      employeeId: artistData?.employeeId || null,
      employeeName: artistData?.employeeName || null,
    };
    onServicesChange(updated);
  };

  const openArtistDialog = (index) => {
    setArtistDialog({ open: true, index });
  };
  React.useEffect(() => {
    if (onArtistDialogOpen) onArtistDialogOpen(openArtistDialog);
  }); // eslint-disable-line react-hooks/exhaustive-deps

  const isServiceAdded = (svcId) => selectedServices.some(s => s.serviceId === svcId);

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" style={{ color: '#EF60A3' }} />
      </div>
    );
  }

  if (error && !apiCategories) {
    return (
      <div className="text-center py-4">
        <p className="small mb-2" style={{ color: '#E91E63' }}>Error al cargar servicios</p>
        <button onClick={refetch} className="btn btn-link btn-sm" style={{ color: '#EF60A3' }}>Reintentar</button>
      </div>
    );
  }

  return (
    <div>
      {/* Selected services as Cards — shown at top so they're visible on mobile */}
      {selectedServices.length > 0 && (
        <div className="mb-3 pb-3" style={{ borderBottom: '2px solid #FCE4EC' }}>
          <div className="d-flex align-items-center gap-2 mb-2">
            <Badge pill style={{ backgroundColor: '#EF60A3', fontSize: '0.7rem' }}>
              {selectedServices.length}
            </Badge>
            <small className="fw-semibold" style={{ color: '#EF60A3' }}>
              servicio{selectedServices.length > 1 ? 's' : ''} seleccionado{selectedServices.length > 1 ? 's' : ''}
            </small>
          </div>

          <Row className="g-2">
            {selectedServices.map((svc, idx) => (
              <Col xs={6} sm={3} key={idx}>
                <Card
                  className="h-100 position-relative"
                  style={{
                    backgroundColor: '#FFF8FA',
                    border: '1px solid #FCE4EC',
                    borderRadius: 12,
                  }}
                >
                  <button
                    onClick={() => handleRemoveService(idx)}
                    className="btn btn-sm p-0 position-absolute"
                    title="Quitar"
                    style={{ top: 6, right: 6, color: '#E57373', lineHeight: 1, zIndex: 1 }}
                  >
                    <X size={14} />
                  </button>
                  <Card.Body className="p-2 d-flex flex-column align-items-center text-center">
                    <small
                      className="text-uppercase d-block mb-1 w-100"
                      style={{ fontSize: '0.55rem', letterSpacing: '0.04em', color: '#C48BA0' }}
                    >
                      {svc.categoryName}
                    </small>
                    <p
                      className="fw-semibold mb-1 w-100"
                      style={{ fontSize: '0.75rem', color: '#444', overflowWrap: 'anywhere' }}
                    >
                      {svc.shortName || svc.label}
                    </p>
                    {svc.price && (
                      <span className="fw-bold mb-2" style={{ fontSize: '0.8rem', color: '#EF60A3' }}>
                        {svc.price}
                      </span>
                    )}
                    <button
                      onClick={() => setArtistDialog({ open: true, index: idx })}
                      className="btn btn-sm w-100 d-inline-flex align-items-center justify-content-center gap-1 mt-auto"
                      style={{
                        fontSize: '0.65rem',
                        border: '1px solid #F8BBD0',
                        borderRadius: 20,
                        color: svc.employeeName ? '#EF60A3' : '#999',
                        backgroundColor: '#fff',
                        padding: '4px 8px',
                      }}
                    >
                      <User size={10} />
                      <span className="text-truncate">
                        {svc.employeeName || 'Elegir artista'}
                      </span>
                      <ChevronDown size={10} />
                    </button>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      )}

      <h4 className="h5 fw-semibold text-center mb-4" style={{ color: '#333' }}>
        Selecciona tus servicios
      </h4>

      {/* Category pills — active = pink bg + white text, inactive = white bg + pink text */}
      <div className="d-flex flex-wrap justify-content-center gap-2 mb-3">
        {priceData.map((cat, idx) => {
          const isActive = selectedCatIndex === idx;
          const Icon = getCategoryIcon(cat.service_name);
          return (
            <button
              key={idx}
              onClick={() => handleCategorySelect(idx)}
              className="btn btn-sm d-flex flex-column align-items-center gap-1"
              style={{
                minWidth: 72,
                padding: '8px 10px',
                borderRadius: 12,
                fontSize: '0.65rem',
                fontWeight: 500,
                transition: 'all 0.2s',
                backgroundColor: isActive ? '#EF60A3' : '#fff',
                border: '1.5px solid #EF60A3',
                color: isActive ? '#fff' : '#EF60A3',
              }}
            >
              <span
                className="d-inline-flex align-items-center justify-content-center rounded-circle"
                style={{
                  width: 32,
                  height: 32,
                  backgroundColor: isActive ? 'rgba(255,255,255,0.25)' : '#FFF0F5',
                }}
              >
                <Icon size={16} style={{ color: isActive ? '#fff' : '#EF60A3' }} />
              </span>
              <span className="text-nowrap">{cat.service_name}</span>
            </button>
          );
        })}
      </div>

      {/* Sub-category tabs */}
      {selectedCategory?.sub_services?.length > 0 && (
        <div
          className="d-flex flex-nowrap gap-2 mb-3 pb-2"
          style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}
        >
          {selectedCategory.sub_services.map((sub, idx) => {
            const isActive = selectedSubCategory?.sub_service_name === sub.sub_service_name;
            return (
              <button
                key={sub.sub_service_name || idx}
                onClick={() => setSelectedSubCategory(sub)}
                className="btn btn-sm flex-shrink-0"
                style={{
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  color: isActive ? '#fff' : '#EF60A3',
                  backgroundColor: isActive ? '#EF60A3' : '#FFF0F5',
                  border: '1.5px solid #EF60A3',
                  borderRadius: 20,
                  padding: '6px 16px',
                  whiteSpace: 'nowrap',
                }}
              >
                {sub.sub_service_name}
              </button>
            );
          })}
        </div>
      )}

      {/* Services list */}
      {visibleServices.length > 0 && (
        <div>
          {visibleServices.map((svc, idx) => {
            const added = isServiceAdded(svc.id);
            const selectedIdx = added
              ? selectedServices.findIndex((s) => s.serviceId === svc.id)
              : -1;
            const selectedEntry = selectedIdx >= 0 ? selectedServices[selectedIdx] : null;
            return (
              <div
                key={svc.id || idx}
                className="d-flex align-items-center justify-content-between flex-wrap py-2 px-1 gap-1"
                style={{ borderBottom: '1px solid #f5f5f5' }}
              >
                <div className="d-flex align-items-center gap-2" style={{ minWidth: 0, flex: 1 }}>
                  <span
                    className="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                    style={{ width: 28, height: 28, backgroundColor: '#FFF0F5' }}
                  >
                    <Sparkles size={12} style={{ color: '#F49BC3' }} />
                  </span>
                  {/* El nombre completo, con salto de línea si es largo (sin "…") */}
                  <span className="small fw-medium" style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{svc.name}</span>
                </div>
                <div className="d-flex align-items-center gap-2 flex-shrink-0 ms-auto">
                  {svc.price && (
                    <span className="small fw-bold" style={{ color: '#EF60A3' }}>{svc.price}</span>
                  )}
                  {/* Recién agregado: elegir artista aquí mismo, sin buscar la tarjeta */}
                  {added && selectedIdx >= 0 && (
                    <button
                      onClick={() => openArtistDialog(selectedIdx)}
                      className="btn btn-sm rounded-pill d-inline-flex align-items-center gap-1"
                      style={{
                        fontSize: '0.7rem',
                        fontWeight: 500,
                        padding: '3px 10px',
                        backgroundColor: '#fff',
                        color: selectedEntry?.employeeName ? '#EF60A3' : '#999',
                        border: '1px solid #F8BBD0',
                      }}
                    >
                      <User size={10} />
                      {selectedEntry?.employeeName || 'Elegir artista'}
                      <ChevronDown size={10} />
                    </button>
                  )}
                  {svc.id && (
                    <button
                      onClick={() => !added && handleAddService(svc)}
                      disabled={added}
                      className="btn btn-sm rounded-pill"
                      style={{
                        fontSize: '0.7rem',
                        fontWeight: 500,
                        padding: '3px 12px',
                        backgroundColor: added ? '#E8F5E9' : '#FFF0F5',
                        color: added ? '#388E3C' : '#EF60A3',
                        border: added ? '1px solid #C8E6C9' : '1px solid #F8BBD0',
                      }}
                    >
                      {added ? 'Agregado' : 'Anadir'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Artist Picker Dialog */}
      {artistDialog.index !== null && (
        <ArtistPickerDialog
          open={artistDialog.open}
          onOpenChange={(open) => setArtistDialog(prev => ({ ...prev, open }))}
          serviceId={selectedServices[artistDialog.index]?.serviceId}
          serviceName={selectedServices[artistDialog.index]?.shortName || selectedServices[artistDialog.index]?.label}
          onSelect={handleArtistSelect}
        />
      )}
    </div>
  );
};

export default ServiceWithArtistSelector;
